import { z } from 'zod'

/**
 * Validação centralizada das envs do front.
 *
 * Precedência (alto → baixo) para decidir o "modo" efetivo:
 *   1. Preferência salva no `localStorage` (escolhida pelo usuário no
 *      `FirstBootModePicker` ou no `ModeSwitcherPopover`).
 *   2. `import.meta.env.VITE_*` (definida pelo build / `.env.[mode]`).
 *
 * Por que essa ordem:
 * - O modo do build é o "default sensato" (mocks em dev, api em prod).
 * - O override de localStorage é "vontade explícita do humano operando o
 *   navegador". Ganha do build em qualquer cenário — inclusive prod, onde
 *   é útil para demos com MSW ou para apontar uma instância prod contra
 *   uma API alternativa sem novo deploy.
 *
 * O retorno de `getAppEnv()` é a fonte da verdade do "modo" do app
 * (`mocks` | `api`). NUNCA leia `import.meta.env.VITE_USE_MOCKS`
 * diretamente em outro lugar.
 */

const boolFromEnv = z.preprocess((val) => {
  if (typeof val === 'boolean') return val
  if (typeof val === 'string') {
    const s = val.toLowerCase().trim()
    if (s === 'true' || s === '1') return true
    if (s === 'false' || s === '0' || s === '') return false
  }
  return val
}, z.boolean())

const RawEnvSchema = z.object({
  VITE_USE_MOCKS: boolFromEnv.default('true'),
  VITE_API_BASE_URL: z.string().trim().default(''),
  VITE_WS_URL: z.string().trim().default(''),
  VITE_AUTH_PROVIDER_URL: z.string().trim().default(''),
})

export type AppMode = 'mocks' | 'api'

export interface AppEnv {
  mode: AppMode
  apiBaseUrl: string
  wsUrl: string
  authProviderUrl: string
  isProduction: boolean
  /**
   * `true` se o modo / URL veio do `localStorage` (override explícito do
   * usuário) e não do `.env`. Útil para o ModeSwitcherPopover mostrar
   * "Override ativo".
   */
  hasOverride: boolean
}

export class AppEnvError extends Error {
  override readonly name = 'AppEnvError'
  constructor(
    message: string,
    readonly help?: string,
    /** Quando true, a tela de erro fatal pode oferecer "voltar para mocks". */
    readonly canFallbackToMocks = false,
  ) {
    super(message)
  }
}

export interface RawEnv {
  VITE_USE_MOCKS?: string | undefined
  VITE_API_BASE_URL?: string | undefined
  VITE_WS_URL?: string | undefined
  VITE_AUTH_PROVIDER_URL?: string | undefined
}

// -----------------------------------------------------------------------------
// Storage de preferência (override do usuário)
// -----------------------------------------------------------------------------

const STORAGE_KEY = 'vectraclip-mode-preference'

/**
 * Override gravado pelo usuário. Quando presente, ganha de `VITE_USE_MOCKS`.
 * `apiBaseUrl` é opcional — quando ausente em modo `api`, o app cai no valor
 * de `VITE_API_BASE_URL` (e em fallback erro fatal se estiver vazio).
 */
export interface ModePreference {
  mode: AppMode
  apiBaseUrl?: string | undefined
}

const ModePreferenceSchema = z.object({
  mode: z.union([z.literal('mocks'), z.literal('api')]),
  apiBaseUrl: z.string().trim().optional(),
})

export function readStoredPreference(): ModePreference | null {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return null

  // Storage corrompido (JSON inválido) OU schema fora do shape conhecido →
  // limpa e ignora. Em vez de travar o app, voltamos para o "primeiro boot".
  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    window.localStorage.removeItem(STORAGE_KEY)
    return null
  }

  const parsed = ModePreferenceSchema.safeParse(json)
  if (!parsed.success) {
    window.localStorage.removeItem(STORAGE_KEY)
    return null
  }
  return parsed.data
}

export function writeStoredPreference(pref: ModePreference): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pref))
}

export function clearStoredPreference(): void {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(STORAGE_KEY)
}

/**
 * VectraClaw monta REST sob `/api`. Aceita base com ou sem esse sufixo
 * (deploy antigo só com host, ou `.env.api` já com `/api`).
 */
export function normalizeApiBaseUrl(url: string): string {
  const trimmed = url.trim().replace(/\/+$/, '')
  if (!trimmed) return trimmed
  if (trimmed.endsWith('/api')) return trimmed
  return `${trimmed}/api`
}

/** localhost / 127.0.0.1 — override de dev não pode vazar para build de produção. */
export function isLoopbackUrl(url: string): boolean {
  const trimmed = url.trim()
  if (!trimmed) return false
  try {
    const host = new URL(trimmed).hostname
    return host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
  } catch {
    return /localhost|127\.0\.0\.1/i.test(trimmed)
  }
}

function dropLoopbackPreferenceOnProduction(
  isProduction: boolean,
  preference: ModePreference | null,
): ModePreference | null {
  if (!isProduction || !preference?.apiBaseUrl?.trim()) return preference
  if (!isLoopbackUrl(preference.apiBaseUrl)) return preference
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(STORAGE_KEY)
  }
  return null
}

/**
 * `true` no PRIMEIRO boot do navegador (antes do usuário escolher
 * explicitamente um modo). Usado por `main.tsx` para decidir entre montar
 * o app ou o `FirstBootModePicker`.
 *
 * Importante: ter `VITE_USE_MOCKS` no `.env` NÃO conta como "decisão do
 * usuário" — o build sempre traz um default. Só preferência salva conta.
 */
export function isFirstBoot(): boolean {
  return readStoredPreference() === null
}

// -----------------------------------------------------------------------------
// Parser puro
// -----------------------------------------------------------------------------

/**
 * Parser puro. Recebe um objeto cru (env) + uma preferência opcional e
 * retorna o env validado, ou lança `AppEnvError` com mensagem clara.
 *
 * Mantido público para que testes possam exercitar as regras sem stubar
 * `import.meta.env` nem `localStorage`.
 */
export function parseAppEnv(
  raw: RawEnv,
  isProduction = false,
  preference: ModePreference | null = null,
): AppEnv {
  const parsed = RawEnvSchema.safeParse({
    VITE_USE_MOCKS: raw.VITE_USE_MOCKS,
    VITE_API_BASE_URL: raw.VITE_API_BASE_URL,
    VITE_WS_URL: raw.VITE_WS_URL,
    VITE_AUTH_PROVIDER_URL: raw.VITE_AUTH_PROVIDER_URL,
  })

  if (!parsed.success) {
    const flat = parsed.error.flatten().fieldErrors
    throw new AppEnvError(
      'Configuração inválida das variáveis de ambiente VITE_*.',
      JSON.stringify(flat, null, 2),
    )
  }

  const data = parsed.data
  const preferenceResolved = dropLoopbackPreferenceOnProduction(isProduction, preference)

  // Modo: preferência > env. Se o usuário forçou api e o env tem URL
  // diferente, a preferência vence (URL do override ou URL do env como
  // fallback nessa ordem).
  const envMode: AppMode = data.VITE_USE_MOCKS ? 'mocks' : 'api'
  const mode: AppMode = preferenceResolved?.mode ?? envMode
  let apiBaseUrl = preferenceResolved?.apiBaseUrl?.trim() || data.VITE_API_BASE_URL

  if (!apiBaseUrl || apiBaseUrl.includes('undefined') || apiBaseUrl.includes('null')) {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY)
    }
    apiBaseUrl = data.VITE_API_BASE_URL
  } else if (apiBaseUrl.includes('http://localhost:3000')) {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY)
    }
    apiBaseUrl = data.VITE_API_BASE_URL
  } else if (preferenceResolved?.apiBaseUrl && apiBaseUrl) {
    // Preferência só pode ter pathname vazio ou /api. Path adicional (ex.:
    // /api/auth/login) é resultado de alguém colar a URL de um endpoint no
    // ModeSwitcherPopover — o cliente concatena de novo e quebra com 404.
    try {
      const parsedUrl = new URL(apiBaseUrl)
      const cleanPath = parsedUrl.pathname.replace(/\/+$/, '')
      if (cleanPath !== '' && cleanPath !== '/api') {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(STORAGE_KEY)
        }
        apiBaseUrl = data.VITE_API_BASE_URL
      }
    } catch {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY)
      }
      apiBaseUrl = data.VITE_API_BASE_URL
    }
  }

  if (mode === 'api' && !apiBaseUrl) {
    throw new AppEnvError(
      'Modo "api" exige uma URL base preenchida.',
      'Defina `VITE_API_BASE_URL` no .env (ex.: http://localhost:3100/api) ou use o seletor no canto inferior direito para informar a URL e voltar para mocks.',
      true,
    )
  }

  const envApiBaseUrl =
    envMode === 'api' && data.VITE_API_BASE_URL
      ? normalizeApiBaseUrl(data.VITE_API_BASE_URL)
      : data.VITE_API_BASE_URL

  if (mode === 'api' && apiBaseUrl) {
    apiBaseUrl = normalizeApiBaseUrl(apiBaseUrl)
  }

  const hasOverrideResolved =
    preferenceResolved !== null &&
    (preferenceResolved.mode !== envMode || apiBaseUrl !== envApiBaseUrl)

  return {
    mode,
    apiBaseUrl,
    wsUrl: data.VITE_WS_URL,
    authProviderUrl: data.VITE_AUTH_PROVIDER_URL,
    isProduction,
    hasOverride: hasOverrideResolved,
  }
}

// -----------------------------------------------------------------------------
// Singleton (cache)
// -----------------------------------------------------------------------------

let cached: AppEnv | null = null

function buildAppEnvFromRuntime(): AppEnv {
  const meta = import.meta.env as Record<string, string | boolean | undefined>
  return parseAppEnv(
    {
      VITE_USE_MOCKS: meta.VITE_USE_MOCKS as string | undefined,
      VITE_API_BASE_URL: meta.VITE_API_BASE_URL as string | undefined,
      VITE_WS_URL: meta.VITE_WS_URL as string | undefined,
      VITE_AUTH_PROVIDER_URL: meta.VITE_AUTH_PROVIDER_URL as string | undefined,
    },
    Boolean(meta.PROD),
    readStoredPreference(),
  )
}

/** Sempre relê `localStorage` + `.env` — use em UI de modo e no ApiClient. */
export function readAppEnvFresh(): AppEnv {
  return buildAppEnvFromRuntime()
}

export function getAppEnv(): AppEnv {
  if (cached) return cached
  cached = buildAppEnvFromRuntime()
  return cached
}

/** URL REST efetiva (vazia em mocks). Não usa cache — alinhada à preferência salva. */
export function getEffectiveApiBaseUrl(): string {
  const env = readAppEnvFresh()
  return env.mode === 'mocks' ? '' : env.apiBaseUrl
}

export function getAppMode(): AppMode {
  return getAppEnv().mode
}

/** Invalida cache de `getAppEnv()` (ex.: após salvar preferência antes do reload). */
export function invalidateAppEnvCache(): void {
  cached = null
}

/** Resetar cache. Usado por testes E pelo ModeSwitcherPopover antes do reload. */
export function __resetAppEnvForTests(): void {
  invalidateAppEnvCache()
}
